// =============================================================================
// Module Core - Module Access Repository
// Gestion des accès aux modules pour les membres
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface ModuleAccess {
  id: string;
  org_id: string;
  member_id: string;
  module_code: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Récupère tous les accès modules pour une organisation
 */
export async function fetchOrgModuleAccess(orgId: string): Promise<ModuleAccess[]> {
  const { data, error } = await supabase
    .from('core_member_module_access')
    .select('*')
    .eq('org_id', orgId)
    .eq('enabled', true);

  if (error) {
    console.error('Error fetching org module access:', error);
    return [];
  }

  return data as ModuleAccess[];
}

/**
 * Récupère les accès modules pour un membre spécifique
 */
export async function fetchMemberModuleAccess(
  orgId: string,
  memberId: string
): Promise<string[]> {
  const { data, error } = await supabase
    .from('core_member_module_access')
    .select('module_code')
    .eq('org_id', orgId)
    .eq('member_id', memberId)
    .eq('enabled', true);

  if (error) {
    console.error('Error fetching member module access:', error);
    return [];
  }

  return data.map((row) => row.module_code);
}

/**
 * Récupère les accès modules pour l'utilisateur courant
 */
export async function fetchCurrentUserModuleAccess(orgId: string): Promise<string[]> {
  // First get the current user's member record
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return [];

  const { data: memberData, error: memberError } = await supabase
    .from('core_organization_members')
    .select('id')
    .eq('org_id', orgId)
    .eq('user_id', userData.user.id)
    .eq('status', 'active')
    .single();

  if (memberError || !memberData) {
    console.error('Error fetching current user member:', memberError);
    return [];
  }

  return fetchMemberModuleAccess(orgId, memberData.id);
}

// ─────────────────────────────────────────────────────────────────────────────
// MUTATIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Active ou désactive l'accès à un module pour un membre
 */
export async function setMemberModuleAccess(
  orgId: string,
  memberId: string,
  moduleCode: string,
  enabled: boolean
): Promise<boolean> {
  const { error } = await supabase
    .from('core_member_module_access')
    .upsert(
      {
        org_id: orgId,
        member_id: memberId,
        module_code: moduleCode,
        enabled,
      },
      {
        onConflict: 'org_id,member_id,module_code',
      }
    );

  if (error) {
    console.error('Error setting member module access:', error);
    return false;
  }

  return true;
}

/**
 * Supprime l'accès à un module pour un membre
 */
export async function removeMemberModuleAccess(
  orgId: string,
  memberId: string,
  moduleCode: string
): Promise<boolean> {
  const { error } = await supabase
    .from('core_member_module_access')
    .delete()
    .eq('org_id', orgId)
    .eq('member_id', memberId)
    .eq('module_code', moduleCode);

  if (error) {
    console.error('Error removing member module access:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// RPC CHECK
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Vérifie via RPC si l'utilisateur a accès à un module
 */
export async function hasModuleAccess(orgId: string, moduleCode: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('has_module_access', {
    _org_id: orgId,
    _module_code: moduleCode,
  });

  if (error) {
    console.error('Error checking module access:', error);
    return false;
  }

  return data === true;
}
